import Layout from "@/components/Layout/index.ts";
import RootErrorBoundary from "@/components/RootErrorBoundary/index.ts";
import EmployeeProvider from "@/contexts/EmployeeProvider.tsx";
import { CircularProgress } from "@mui/material";
import { Suspense, lazy } from "react";
import { type RouteObject, createBrowserRouter } from "react-router-dom";

const HomePage = lazy(() => import("@/pages/HomePage/HomePage.tsx"));
const PeoplePage = lazy(() => import("@/pages/PeoplePage/PeoplePage.tsx"));
const PlacesPage = lazy(() => import("@/pages/PlacesPage/PlacesPage.tsx"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage/LeadsPage.tsx"));
const CasesPage = lazy(() => import("@/pages/CasesPage/CasesPage.tsx"));
const TasksPage = lazy(() => import("@/pages/TasksPage/TasksPage.tsx"));
const NotificationHistoryPage = lazy(
    () => import("@/pages/NotificationHistoryPage/NotificationHistoryPage.tsx"),
);
const ReportDemandDates = lazy(() => import("@/pages/ReportDemandDates/ReportDemandDates.tsx"));
const ReportLastTouch = lazy(() => import("@/pages/ReportLastTouch/ReportLastTouch.tsx"));
const ReportSol = lazy(() => import("@/pages/ReportSol/ReportSol.tsx"));
const ReportReferrals = lazy(() => import("@/pages/ReportReferrals/ReportReferrals.tsx"));
const CaseDetail = lazy(() => import("@/pages/CaseDetail/CaseDetail.tsx"));
const PartyRoleDetail = lazy(() => import("@/pages/PartyRoleDetail/PartyRoleDetail.tsx"));
const CaseTypeDetail = lazy(() => import("@/pages/CaseTypeDetail/CaseTypeDetail.tsx"));
const PersonDetail = lazy(() => import("@/pages/PersonDetail/PersonDetail.tsx"));
const EmployeeDetail = lazy(() => import("@/pages/EmployeeDetail/EmployeeDetail.tsx"));
const CompanyDetail = lazy(() => import("@/pages/CompanyDetail/CompanyDetail.tsx"));
const AdminUserManagement = lazy(
    () => import("@/pages/AdminUserManagement/AdminUserManagement.tsx"),
);
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage/TemplatesPage.tsx"));
const DocumentTemplatesPage = lazy(
    () => import("@/pages/DocumentTemplatesPage/DocumentTemplatesPage.tsx"),
);
const NoteTemplatesPage = lazy(() => import("@/pages/NoteTemplatesPage/NoteTemplatesPage.tsx"));
const AdminProviderType = lazy(() => import("@/pages/AdminProviderType/AdminProviderType.tsx"));
const PublicClientLogin = lazy(() => import("@/pages/PublicClientLogin/PublicClientLogin.tsx"));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
    <Suspense
        fallback={<CircularProgress sx={{ position: "absolute", top: "50%", left: "50%" }} />}
    >
        {children}
    </Suspense>
);

const pathRoutes: RouteObject[] = [
    {
        path: "/",
        element: (
            <SuspenseWrapper>
                <HomePage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/people",
        element: (
            <SuspenseWrapper>
                <PeoplePage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/places",
        element: (
            <SuspenseWrapper>
                <PlacesPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/leads",
        element: (
            <SuspenseWrapper>
                <LeadsPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/cases",
        element: (
            <SuspenseWrapper>
                <CasesPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/tasks",
        element: (
            <SuspenseWrapper>
                <TasksPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/notification-history",
        element: (
            <SuspenseWrapper>
                <NotificationHistoryPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/reports/demand-dates",
        element: (
            <SuspenseWrapper>
                <ReportDemandDates />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/reports/last-touch",
        element: (
            <SuspenseWrapper>
                <ReportLastTouch />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/reports/sol",
        element: (
            <SuspenseWrapper>
                <ReportSol />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/reports/referrals",
        element: (
            <SuspenseWrapper>
                <ReportReferrals />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/case-detail/:caseId",
        element: (
            <SuspenseWrapper>
                <CaseDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/party-role-detail/:partyRoleUUID",
        element: (
            <SuspenseWrapper>
                <PartyRoleDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/case-type-detail/:caseTypeUUID",
        element: (
            <SuspenseWrapper>
                <CaseTypeDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/person-detail/:personId",
        element: (
            <SuspenseWrapper>
                <PersonDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/employee-detail/:employeeId",
        element: (
            <SuspenseWrapper>
                <EmployeeDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/place-detail/:placeId",
        element: (
            <SuspenseWrapper>
                <CompanyDetail />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/admin/user-management",
        element: (
            <SuspenseWrapper>
                <AdminUserManagement />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/admin/templates",
        element: (
            <SuspenseWrapper>
                <TemplatesPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/admin/document-templates",
        element: (
            <SuspenseWrapper>
                <DocumentTemplatesPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/admin/note-templates",
        element: (
            <SuspenseWrapper>
                <NoteTemplatesPage />
            </SuspenseWrapper>
        ),
    },
    {
        path: "/admin/provider-specialties",
        element: (
            <SuspenseWrapper>
                <AdminProviderType />
            </SuspenseWrapper>
        ),
    },
];

const protectedRoutes: RouteObject = {
    path: "/",
    element: (
        <EmployeeProvider>
            <Layout />
        </EmployeeProvider>
    ),
    errorElement: <RootErrorBoundary />,
    children: pathRoutes,
};

const pathRoutes2: RouteObject[] = [
    {
        path: "/client-case-status",
        element: (
            <SuspenseWrapper>
                <PublicClientLogin />
            </SuspenseWrapper>
        ),
    },
];

const protectedRoutes2: RouteObject = {
    path: "/",
    errorElement: <RootErrorBoundary />,
    children: pathRoutes2,
};

export const router = createBrowserRouter([protectedRoutes, protectedRoutes2]);

if (import.meta.hot) {
    import.meta.hot.dispose(() => router.dispose());
}
